import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from "react"
import { Product } from "@lib/swell/storefront-data-hooks/src/types"
import {
  getProduct,
  getSwellProduct,
} from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import { getPrice } from "@lib/swell/storefront-data-hooks/src/utils/product"
import {
  Selection,
  setDefaultSelections,
} from "@components/ConfiguratorProductForm/ConfiguratorProductForm"
// Define the types for your steps and selections

export type StepProduct = {
  product?: Product
  swellProduct?: any
  variant?: any
  selections?: Selection[]
}

export type Step = {
  id: string
  name: string
  stepProducts?: StepProduct[]
  disabled: boolean
}

// Define the shape of your context state
interface ConfiguratorContextProps {
  steps: Step[]
  currentStepIndex: number
  setCurrentStepIndex: (index: number) => void
  currentStep: Step | undefined
  setConfiguratorProduct: (product: Product) => void
  goToNextStep: () => void
  goToPreviousStep: () => void
  selections: Selection[]
  selectProduct: (stepId: string, productId: string) => void
  updateCurrentStepVariant: (productId: string, variant: any) => void
  updateCurrentStepSelections: (productId: string, selections: any) => void
  currentFormattedTotalPrice: () => string
  loadingConfigurator: boolean
  prevButtonIsEnabled: boolean
  setPrevButtonIsEnabled: (prevButtonIsEnabled: boolean) => void
  nextButtonIsEnabled: boolean
  setNextButtonIsEnabled: (nextButtonIsEnabled: boolean) => void
  loadEstimateForm: () => void
  configuratorEstimateModalIsOpen: boolean
  setConfiguratorEstimateModalIsOpen: (configuratorEstimateModalIsOpen: boolean) => void
}

// Create the context with a default value
const ConfiguratorContext = createContext<ConfiguratorContextProps>({
  steps: [],
  currentStepIndex: 0,
  setCurrentStepIndex: () => {},
  currentStep: { id: "", name: "", disabled: false },
  setConfiguratorProduct: () => {},
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  selections: [],
  selectProduct: () => {},
  updateCurrentStepVariant: () => {},
  updateCurrentStepSelections: () => {},
  currentFormattedTotalPrice: () => "",
  loadingConfigurator: true,
  prevButtonIsEnabled: false,
  setPrevButtonIsEnabled: () => {},
  nextButtonIsEnabled: true,
  setNextButtonIsEnabled: () => {},
  loadEstimateForm: () => {},
  configuratorEstimateModalIsOpen: false,
  setConfiguratorEstimateModalIsOpen: () => {},
})

export const useConfigurator = () => useContext(ConfiguratorContext)

interface ConfiguratorProviderProps {
  children: ReactNode
}

export const ConfiguratorProvider = ({ children }: ConfiguratorProviderProps) => {
  const [steps, setSteps] = useState<Step[]>([])
  const [configuratorProduct, setConfiguratorProduct] = useState<Product>()
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0)
  const [currentStep, setCurrentStep] = useState<Step | undefined>()
  const [selections, setSelections] = useState<Selection[]>([])
  const [loadingConfigurator, setLoadingConfigurator] = useState<boolean>(true)
  const [prevButtonIsEnabled, setPrevButtonIsEnabled] = useState<boolean>(false)
  const [nextButtonIsEnabled, setNextButtonIsEnabled] = useState<boolean>(true)
  const [configuratorEstimateModalIsOpen, setConfiguratorEstimateModalIsOpen] = useState<boolean>(
    false
  )

  useEffect(() => {
    setCurrentStep(steps[currentStepIndex])
    updatePrevNextButtonState(currentStepIndex)
  }, [currentStepIndex, steps])

  useEffect(() => {
    const fetchStepDetails = async () => {
      if (!configuratorProduct?.content?.steps) return
      const stepsWithDetails = await Promise.all(
        configuratorProduct.content.steps.map(async (step: any) => {
          try {
            // Fetch details concurrently for each step
            const stepProductsPromises = step.products_ids.map(
              async (product_id: string, index: number) => {
                let data = {}
                const productDetail = await getProduct({ id: product_id })
                const swellProductDetail = await getSwellProduct({ id: product_id })
                if (productDetail) {
                  const defaultSelections: Selection[] = setDefaultSelections(
                    productDetail?.options
                  )
                  data = {
                    product: productDetail,
                    selections: defaultSelections,
                  }
                }
                if (swellProductDetail) data = { ...data, swellProduct: swellProductDetail }

                return data // Return the data object for each product_id
              }
            )

            step.disabled = true

            // Await the resolution of all promises related to stepProducts
            const stepProducts = await Promise.all(stepProductsPromises)
            return { ...step, stepProducts }
          } catch (error) {
            // Handle any errors that occur during the fetch
            console.error("Error fetching step details:", error)
            // Optionally, return the step without details or modify as needed
            return step
          }
        })
      )

      // Update state once all promises are resolved
      stepsWithDetails[0].disabled = false
      setSteps(stepsWithDetails)
      setLoadingConfigurator(false)
    }

    fetchStepDetails()
    // Make sure to include all dependencies this effect relies on
    // If getProduct or getSwellProduct are defined outside of this effect and do not change,
    // they do not need to be included as dependencies.
  }, [configuratorProduct?.content.steps]) // This assumes product.content.steps is stable and won't change unexpectedly

  const updateCurrentStepVariant = (productId: string, variant: any) => {
    setSteps((currentSteps) => {
      return currentSteps.map((step, index) => {
        if (index === currentStepIndex && step.stepProducts) {
          // Update only the matching stepProduct of the current step
          const updatedStepProducts = step.stepProducts.map((stepProduct) => {
            if (stepProduct.product && stepProduct.product.id === productId) {
              // Update this stepProduct with the new variant
              return { ...stepProduct, variant }
            }
            return stepProduct
          })

          return { ...step, stepProducts: updatedStepProducts }
        }
        return step
      })
    })
  }

  const updateCurrentStepSelections = (productId: string, selections: Selection[]) => {
    setSteps((currentSteps) => {
      return currentSteps.map((step, index) => {
        if (index === currentStepIndex && step.stepProducts) {
          // Update only the matching stepProduct of the current step
          const updatedStepProducts = step.stepProducts.map((stepProduct) => {
            if (stepProduct.product && stepProduct.product.id === productId) {
              stepProduct.selections?.map((selection) => {
                if (stepProduct.product?.options) {
                  const selectedOption = stepProduct.product?.options.find(
                    (option) => option.id === selection.optionId
                  )

                  const selectedValue = selectedOption?.values.find(
                    (value) => value.id === selection.id
                  )
                  selection.priceForDisplay = selectedValue?.price
                    ? getPrice(selectedValue.price, "USD")
                    : "Included"
                }
              })

              // Update this stepProduct with the new selections
              return { ...stepProduct, selections }
            }

            return stepProduct
          })

          return { ...step, stepProducts: updatedStepProducts }
        }
        return step
      })
    })
  }

  const selectProduct = (stepId: string, productId: string) => {
    setSelections({
      ...selections,
      [stepId]: productId,
    })
  }

  const updatePrevNextButtonState = (currentStepIndex: number) => {
    if (currentStepIndex === 0) {
      setPrevButtonIsEnabled(false)
      setNextButtonIsEnabled(true)
    }

    if (currentStepIndex > 0 && currentStepIndex < steps.length - 1) {
      setPrevButtonIsEnabled(true)
      setNextButtonIsEnabled(true)
    }

    if (currentStepIndex === steps.length - 1) {
      setPrevButtonIsEnabled(true)
      setNextButtonIsEnabled(false)
    }
  }

  const loadEstimateForm = () => {
    console.log("Loading Estimate Form Modal")

    setConfiguratorEstimateModalIsOpen(true)
  }

  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      steps[currentStepIndex + 1].disabled = false
      setCurrentStepIndex(currentStepIndex + 1)
    }
  }

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }

  const currentFormattedTotalPrice = () => {
    // Check if there are no steps or steps without stepProducts
    if (
      steps.length === 0 ||
      steps.every((step) => !(step.stepProducts && step.stepProducts.length > 0))
    ) {
      return getPrice(0, "USD")
    }

    const totalPrice = steps.reduce((acc, step) => {
      // If stepProducts exists and has length, calculate their prices
      if (step.stepProducts && step.stepProducts.length > 0) {
        const stepPrice = step.stepProducts.reduce((accProd, stepProduct) => {
          let price = 0
          // Prefer variant's price if available
          if (stepProduct.variant) {
            price = stepProduct.variant.price
          } else {
            // Use product sale price if on sale, otherwise use its original price
            price = stepProduct?.product?.price || 0
          }
          return accProd + price
        }, 0)

        return acc + stepPrice
      }

      return acc
    }, 0)

    // Assuming getPrice can format the total price based on currency
    return getPrice(totalPrice, "USD")
  }
  // Define validation logic here if necessary

  return (
    <ConfiguratorContext.Provider
      value={{
        steps,
        currentStepIndex,
        setCurrentStepIndex,
        currentStep,
        goToNextStep,
        goToPreviousStep,
        selections,
        selectProduct,
        setConfiguratorProduct,
        updateCurrentStepVariant,
        updateCurrentStepSelections,
        currentFormattedTotalPrice,
        loadingConfigurator,
        prevButtonIsEnabled,
        setPrevButtonIsEnabled,
        nextButtonIsEnabled,
        setNextButtonIsEnabled,
        loadEstimateForm,
        configuratorEstimateModalIsOpen,
        setConfiguratorEstimateModalIsOpen,
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  )
}
